import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  Search, Menu, User, ChevronDown, LogOut, Settings, Bookmark, Star,
  LayoutDashboard, Store, Shield, Home, Compass, Building2, CalendarDays, Tag, CheckCircle2, Zap, TrendingUp, Calendar, ShoppingCart, BookOpen, Leaf, Ticket
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { authService } from "@/api/services/authService";
import { cartService } from "@/api/services/cartService";
import { useAuth } from "@/lib/AuthContext";
import { useQuery } from "@tanstack/react-query";
import ThemeToggle from "./components/shared/ThemeToggle";
import { LangProvider, useLang } from "./components/shared/LangProvider";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import NotificationCenter from "./components/shared/NotificationCenter";
import EmailVerificationBanner from "./components/shared/EmailVerificationBanner";
import OfflineBanner from "./components/shared/OfflineBanner";
import CookieBanner from "./components/shared/CookieBanner";
import AppInstallBanner from "./components/shared/AppInstallBanner";
import ContactSpeedDial from "./components/shared/ContactSpeedDial";
import { initMetaPixelIfConsented, pixelPageView } from "@/utils/metaPixel";

const dashboardPages = [
  "OwnerDashboard", "AddBusiness", "EditBusiness", "ManageReviews", "ManageEnquiries",
  "OwnerAnalytics", "TeamMembers", "OwnerSettings", "ManageEvents", "ManageDeals", "ManageBookings", "BusinessBookingSetup", "AvailabilityManagement",
  "ManageAdvertisements", "ManageGallery", "BookingAnalytics", "OwnerBilling",
  "FollowingBusinesses",
  "AdminDashboard", "AdminBusinesses", "AdminUsers", "AdminReviews", "AdminAnalytics",
  "AdminCategories", "AdminReports", "AdminVerifications", "AdminDeals", "AdminEvents", "AdminBookings", "AdminBlog", "AdminFeedback",
  "AdminImport", "AdminAuditLog", "AdminExperiences", "AdminCampaigns",
  "UserDashboard", "MyBookings", "MyCalendar", "SavedBusinesses", "SavedDeals", "MyReviews", "ProfileSettings", "Notifications",
  "MyOrders", "MyEnquiries", "MyDeals", "MyClaims", "ManageOrders", "ManageMenu", "MyBusinesses", "OwnerVerification", "ManageExperiences"
];

function LayoutInner({ children, currentPageName }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();
  const isDashboard = dashboardPages.includes(currentPageName);

  // Meta Pixel: load once for already-consented visitors, then fire a PageView
  // on every SPA route change (the base snippet already fires the first PageView
  // on load, so skip the initial run here to avoid double-counting).
  const firstRoute = useRef(true);
  useEffect(() => {
    initMetaPixelIfConsented();
  }, []);
  useEffect(() => {
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    pixelPageView();
  }, [location.pathname]);

  const { data: cartCount = 0 } = useQuery({
    queryKey: ["cart-count"],
    queryFn: () => cartService.getAll().then((carts) =>
      carts.reduce((sum, c) =>
        sum + (c.items ?? []).reduce((s, item) => s + (Number(item.quantity) || 1), 0), 0)
    ),
    enabled: !!user,
    staleTime: 30 * 1000,
  });

  const handleLogin = () => navigate(createPageUrl("Login"));
  const handleLogout = async () => { await logout(); navigate(createPageUrl("Home")); };

  const publicNav = [
    { name: t("home"), page: "Home", icon: Home },
    { name: t("explore"), page: "Explore", icon: Compass },
    { name: t("deals"), page: "Deals", icon: Tag },
    { name: "Events", page: "Events", icon: CalendarDays },
    { name: "Blog", page: "Blog", icon: BookOpen },
    { name: t("about"), page: "About", icon: Building2 },
  ];

  if (isDashboard) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={createPageUrl("Home")} className="flex items-center gap-2 flex-shrink-0">
              <img src="/icons/icon-192.png" alt="FindGambia" className="h-8 w-8 rounded-lg" />
              <span className="font-bold text-lg text-foreground hidden sm:block">FindGambia</span>
            </Link>

            {/* Center Nav - Desktop */}
            <nav className="hidden md:flex items-center gap-1">
              {publicNav.map((item) => (
                <Link
                  key={item.page}
                  to={createPageUrl(item.page)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPageName === item.page
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              <Link to={createPageUrl("Explore")}>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg">
                  <Search className="h-4 w-4" />
                </Button>
              </Link>

              {user && (
                <Link to={createPageUrl("Cart")} className="relative">
                  <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg">
                    <ShoppingCart className="h-4 w-4" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                        {cartCount > 9 ? "9+" : cartCount}
                      </span>
                    )}
                  </Button>
                </Link>
              )}

              {user && <NotificationCenter />}

              <ThemeToggle />

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-9 gap-2 rounded-lg px-2">
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                        {user.profileImageUrl ? (
                          <img src={user.profileImageUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-xs font-semibold text-primary">
                            {user.firstName?.[0] || user.email?.[0] || "U"}
                          </span>
                        )}
                      </div>
                      <span className="hidden sm:block text-sm font-medium max-w-[100px] truncate">
                        {user.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "Account"}
                      </span>
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium">{user.firstName} {user.lastName}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {user?.status !== "ACTIVE" && !user?.emailVerified && (
                      <DropdownMenuItem onClick={() => navigate(createPageUrl("AccountActivation"))}>
                        <CheckCircle2 className="h-4 w-4 mr-2 text-primary" /> Activate Account
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("UserDashboard"))}>
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("SavedBusinesses"))}>
                      <Bookmark className="h-4 w-4 mr-2" /> Saved Places
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("SavedDeals"))}>
                      <Tag className="h-4 w-4 mr-2" /> Saved Deals
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("MyBookings"))}>
                      <Calendar className="h-4 w-4 mr-2" /> My Bookings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("MyTickets"))}>
                      <Ticket className="h-4 w-4 mr-2" /> My Tickets
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("MyReviews"))}>
                      <Star className="h-4 w-4 mr-2" /> My Reviews
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(createPageUrl("ProfileSettings"))}>
                      <Settings className="h-4 w-4 mr-2" /> Settings
                    </DropdownMenuItem>
                    <>
                      <DropdownMenuSeparator />
                      {(user.role === "BUSINESS_OWNER" || user.role === "ADMIN" || user.role === "SUPER_ADMIN") ? (
                        <>
                          <DropdownMenuItem onClick={() => navigate(createPageUrl("OwnerDashboard"))}>
                            <Store className="h-4 w-4 mr-2" /> Business Dashboard
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(createPageUrl("BusinessBookingSetup"))}>
                            <Calendar className="h-4 w-4 mr-2" /> Manage Bookings
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(createPageUrl("AddBusiness"))}>
                            <Store className="h-4 w-4 mr-2" /> Add Business
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <DropdownMenuItem onClick={() => navigate(createPageUrl("AddBusiness"))}>
                          <Store className="h-4 w-4 mr-2" /> List Your Business
                        </DropdownMenuItem>
                      )}
                    </>
                    {(user.role === "ADMIN" || user.role === "SUPER_ADMIN") && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate(createPageUrl("AdminDashboard"))}>
                          <Shield className={`h-4 w-4 mr-2 ${user.role === "SUPER_ADMIN" ? "text-purple-500" : ""}`} />
                          {user.role === "SUPER_ADMIN" ? "Super Admin Panel" : "Admin Panel"}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" /> Log Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Button onClick={() => navigate(createPageUrl("Login"))} variant="outline" size="sm" className="rounded-lg font-medium text-xs sm:text-sm">
                    Sign In
                  </Button>
                  <Button onClick={() => navigate(createPageUrl("Signup"))} size="sm" className="rounded-lg font-medium text-xs sm:text-sm hidden sm:flex">
                    Sign Up Free
                  </Button>
                </div>
              )}

              {/* Mobile menu */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg md:hidden">
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 p-0">
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-8">
                      <img src="/icons/icon-192.png" alt="FindGambia" className="h-8 w-8 rounded-lg" />
                      <span className="font-bold text-lg">FindGambia</span>
                    </div>
                    <nav className="space-y-1">
                      {publicNav.map((item) => (
                        <Link
                          key={item.page}
                          to={createPageUrl(item.page)}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            currentPageName === item.page
                              ? "bg-primary/10 text-primary"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent"
                          }`}
                        >
                          <item.icon className="h-4 w-4" />
                          {item.name}
                        </Link>
                      ))}
                      {!user && (
                        <>
                          <div className="pt-4 pb-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3">Account</p>
                          </div>
                          <button
                            onClick={() => { setMobileOpen(false); navigate(createPageUrl("Login")); }}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                          >
                            <User className="h-4 w-4" />
                            {t("signIn")}
                          </button>
                        </>
                      )}
                    </nav>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <OfflineBanner />
      <EmailVerificationBanner />

      {/* Main Content */}
      <main><ErrorBoundary>{children}</ErrorBoundary></main>

      <ContactSpeedDial />
      <CookieBanner />

      {/* Footer */}
      <footer className="border-t border-border bg-muted mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img src="/icons/icon-192.png" alt="FindGambia" className="h-8 w-8 rounded-lg" />
                <span className="font-bold text-lg">FindGambia</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Discover the best local businesses in The Gambia. Your trusted guide to restaurants, hotels, and more.
              </p>
              <a
                href="https://play.google.com/store/apps/details?id=com.findgambia.app"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get FindGambia on Google Play"
                className="mt-4 inline-flex items-center gap-2.5 rounded-lg bg-foreground text-background px-4 py-2 hover:opacity-90 transition-opacity"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                  <path d="M3.609 1.814 13.792 12 3.61 22.186a1.005 1.005 0 0 1-.61-.92V2.734c0-.379.234-.717.61-.92zM14.499 12.707l2.807 2.807-11.02 6.276 8.213-9.083zm3.71-3.71 2.65 1.51c.68.386.68 1.37 0 1.756l-2.65 1.51-3.03-3.03 3.03-3.03zM5.286 1.73l11.02 6.277-2.807 2.807L5.286 1.73z"/>
                </svg>
                <span className="flex flex-col leading-tight text-left">
                  <span className="text-[10px] uppercase tracking-wide opacity-80">Get it on</span>
                  <span className="text-sm font-semibold -mt-0.5">Google Play</span>
                </span>
              </a>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3">Discover</h4>
              <div className="space-y-2">
                <Link to={createPageUrl("Blog")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
                <Link to={createPageUrl("Explore")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Explore</Link>
                <Link to={createPageUrl("Explore") + "?category=restaurant"} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Restaurants</Link>
                <Link to={createPageUrl("Explore") + "?category=hotel"} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Hotels</Link>
                <Link to={createPageUrl("AreaGuide")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Area Guides</Link>
                <Link to={createPageUrl("TourismGuide")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Tourism Guide</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3">For Business</h4>
              <div className="space-y-2">
                <button
                  onClick={() => user ? navigate(createPageUrl("AddBusiness")) : navigate(createPageUrl("Login") + `?returnUrl=${encodeURIComponent(createPageUrl("AddBusiness"))}`)}
                  className="block text-sm text-muted-foreground hover:text-foreground transition-colors text-left"
                >Add Business</button>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3">Company</h4>
              <div className="space-y-2">
                <Link to={createPageUrl("About")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">About</Link>
                <Link to={createPageUrl("Contact")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
                <Link to={createPageUrl("FAQ")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</Link>
                <Link to={createPageUrl("UserGuide")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">User Guide</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="font-semibold text-sm mb-3">Trust & Safety</h4>
                <div className="space-y-2">
                  <Link to={createPageUrl("ContentGuidelines")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Content Guidelines</Link>
                  <Link to={createPageUrl("AccessibilityStatement")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Accessibility Statement</Link>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-3">Legal</h4>
                <div className="space-y-2">
                  <Link to={createPageUrl("TermsOfService")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link>
                  <Link to={createPageUrl("PrivacyPolicy")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
                  <Link to={createPageUrl("CookiePolicy")} className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Cookie Policy</Link>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">&copy; {new Date().getFullYear()} FindGambia. All rights reserved.</p>
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/profile.php?id=61572086764302" target="_blank" rel="noopener noreferrer" aria-label="FindGambia on Facebook" className="text-muted-foreground hover:text-foreground transition-colors">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.youtube.com/@findgambia" target="_blank" rel="noopener noreferrer" aria-label="FindGambia on YouTube" className="text-muted-foreground hover:text-foreground transition-colors">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                <a href="https://www.instagram.com/findgambia26" target="_blank" rel="noopener noreferrer" aria-label="FindGambia on Instagram" className="text-muted-foreground hover:text-foreground transition-colors">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
                </a>
                <a href="https://www.tiktok.com/@findgambia" target="_blank" rel="noopener noreferrer" aria-label="FindGambia on TikTok" className="text-muted-foreground hover:text-foreground transition-colors">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function Layout({ children, currentPageName }) {
  return (
    <LangProvider>
      <LayoutInner currentPageName={currentPageName}>{children}</LayoutInner>
      <AppInstallBanner />
    </LangProvider>
  );
}